import { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { Landing } from "./pages/Landing";
import { LearnMore } from "./pages/LearnMore";
import { Book } from "./pages/Book";
import { BetaLogin } from "./pages/BetaLogin";
import { ResetPassword } from "./pages/ResetPassword";
import { RequestAccess } from "./pages/RequestAccess";
import { Protocol } from "./pages/Protocol";
import { ProtocolSelect } from "./pages/ProtocolSelect";
import { SleepProtocol } from "./pages/SleepProtocol";
import Firewall from "./pages/Firewall";
import { Progress } from "./pages/Progress";
import { Reflection } from "./pages/Reflection";
import { Settings } from "./pages/Settings";
import { Admin } from "./pages/Admin";
import { Upgrade } from "./pages/Upgrade";
import { UpgradeSuccess } from "./pages/UpgradeSuccess";
import { ScenarioLab } from "./pages/ScenarioLab";
import { ScenarioProtocol } from "./pages/ScenarioProtocol";
import { BookVerification } from "./pages/BookVerification";
// ClarityOS v2.1 Pages
import { Baseline } from "./pages/Baseline";
import { TeamsLanding } from "./pages/TeamsLanding";

import { WhatNext } from "./pages/WhatNext";
import { Framework } from "./pages/Framework";
import { Layout } from "./components/Layout";
import { BiofeedbackProvider } from "./contexts/BiofeedbackContext";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip auth check on login page
    if (window.location.pathname === '/login') {
      setLoading(false);
      return;
    }
    
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return children({ user, setUser, loading, checkAuth });
};

// Framework Redirect - serves static HTML
const FrameworkRedirect = () => {
  useEffect(() => {
    window.location.href = '/framework.html';
  }, []);
  return null;
};

// Auth Callback Component
const AuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await axios.post(`${API}/auth/session`, 
            { session_id: sessionId },
            { withCredentials: true }
          );
          setUser(response.data);
          // Clear hash and navigate to dashboard
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/progress', { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-clarity-bg flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-400">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-clarity-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check if user was passed from AuthCallback
  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user must reset password
  if (user.must_reset_password && location.pathname !== '/reset-password') {
    return <Navigate to="/reset-password" state={{ fromLogin: true }} replace />;
  }

  return children;
};

// App Router
const AppRouter = ({ user, setUser, loading, checkAuth }) => {
  const location = useLocation();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          user ? <Navigate to="/progress" replace /> : <Landing />
        } 
      />
      <Route 
        path="/learn-more" 
        element={<LearnMore />} 
      />
      <Route 
        path="/book" 
        element={<Book />} 
      />
      <Route 
        path="/login" 
        element={
          user ? <Navigate to="/progress" replace /> : <BetaLogin setUser={setUser} />
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <ResetPassword user={user} />
        } 
      />
      <Route 
        path="/request-access" 
        element={
          user ? <Navigate to="/progress" replace /> : <RequestAccess />
        } 
      />
      <Route 
        path="/book-verification" 
        element={<BookVerification />} 
      />
      <Route 
        path="/teams" 
        element={<TeamsLanding />} 
      />
      {/* /dashboard → /progress redirect (bookmarks preserved) */}
      <Route 
        path="/dashboard" 
        element={<Navigate to="/progress" replace />}
      />
      <Route 
        path="/protocol" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <ProtocolSelect user={user} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/protocol/neuroplasticity" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Protocol user={user} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sleep" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <SleepProtocol user={user} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/firewall" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Firewall user={user} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/progress" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <Progress user={user} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      {/* ClarityOS v2 Routes */}
      <Route 
        path="/framework"
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <Framework user={user} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/baseline" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <Baseline user={user} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reflection" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <Reflection user={user} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      {/* Redirect old /journal to /reflection */}
      <Route 
        path="/journal" 
        element={<Navigate to="/reflection" replace />} 
      />
      {/* What Next - Post-session screen */}
      <Route 
        path="/what-next" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <WhatNext user={user} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <Settings user={user} setUser={setUser} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <Admin user={user} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upgrade" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Upgrade user={user} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upgrade/success" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <UpgradeSuccess user={user} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scenarios" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <Layout user={user} setUser={setUser}>
              <ScenarioLab user={user} />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scenario/:scenarioId" 
        element={
          <ProtectedRoute user={user} loading={loading}>
            <ScenarioProtocol user={user} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/framework" 
        element={<FrameworkRedirect />} 
      />
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthContext>
        {({ user, setUser, loading, checkAuth }) => (
          <>
            <BiofeedbackProvider>
              <AppRouter 
                user={user} 
                setUser={setUser} 
                loading={loading} 
                checkAuth={checkAuth} 
              />
            </BiofeedbackProvider>
            <Toaster position="top-right" richColors />
          </>
        )}
      </AuthContext>
    </BrowserRouter>
  );
}

export default App;
